import React, { useState, useEffect } from 'react';

import { Grid } from '@mui/material';

import { useSnackbar } from 'hooks';

import CustomDialog from 'components/shared/CustomDialog';
import CustomTextField from 'components/shared/CustomTextField';

import { sleep } from 'utils/async';
import { arrayValidation } from 'utils/array';

import type { Column, DataConfigTable } from 'api/shared/models';

import { ERROR_IN_SOME_FIELDS } from 'constants/messages';

import DialogHeader from './DialogHeader';
import AvailableColumns from './AvailableColumns';
import MyColumns from './MyColumns';

import { getColumnsByConfiguration } from './helpers';
import { styles } from './styles';

interface Props {
  open: boolean;
  tableName: string;
  dataColumns: DataConfigTable;
  showConfigName?: boolean;
  configurationName?: string;
  handleClose: () => void;
  handleDelete?: () => void;
  handleUpdateColumns: (newColumns: Column[], configName: string) => void;
}

const ConfigurationTableModal = ({
  open,
  handleClose,
  tableName,
  handleUpdateColumns,
  dataColumns,
  showConfigName = false,
  configurationName = '',
  handleDelete,
}: Props) => {
  const { showSnackbarWarning } = useSnackbar();

  const [availableColumns, setAvailableColumns] = useState<Column[]>([]);
  const [myColumns, setMyColumns] = useState<Column[]>([]);
  const [configName, setConfigName] = useState<string>(configurationName);
  const [errorConfigName, setErrorConfigName] = useState<string>('');
  const [isSubmitting, setSubmitting] = useState<boolean>(false);

  const moveRows = (rowIndex: number, newIndex: number) => {
    const newMyColumns = [...myColumns];
    const dragColumn = myColumns[rowIndex];

    newMyColumns.splice(rowIndex, 1);
    newMyColumns.splice(newIndex, 0, dragColumn);
    setMyColumns(newMyColumns);
  };

  const startProcessByColumns = (columns: Column[]) => {
    const { newAvailableColumns, newMyColumns } =
      getColumnsByConfiguration(columns);
    setMyColumns(newMyColumns);
    setAvailableColumns(newAvailableColumns);
  };

  const handleSubmit = () => {
    setSubmitting(true);
    const newColumns = [...myColumns, ...availableColumns];
    if (!configName && showConfigName) {
      showSnackbarWarning(ERROR_IN_SOME_FIELDS);
      setErrorConfigName('Es necesario ingresar un valor');
      return;
    }

    handleUpdateColumns(newColumns, configName);
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    setConfigName(value);
    if (!value) {
      setErrorConfigName('Es necesario ingresar un valor');
    } else {
      setErrorConfigName('');
    }
  };

  const handleReset = () => {
    startProcessByColumns(dataColumns?.originalColumns || []);
  };

  const handleChangeAvailableColumns = async (column: Column) => {
    await sleep(100);
    const newColumn = availableColumns.find(
      (item) => item.apiField === column.apiField,
    );
    if (newColumn) {
      setMyColumns([...myColumns, { ...newColumn, status: true }]);
    }

    if (arrayValidation(availableColumns)) {
      const newAvailableColumns = availableColumns.filter(
        (item) => item.apiField !== column.apiField,
      );
      setAvailableColumns(newAvailableColumns);
    }
  };

  const handleChangeMyColumns = async (column: Column) => {
    await sleep(100);
    const newAvailableColumns = [
      ...availableColumns,
      { ...column, status: false },
    ];
    setAvailableColumns(newAvailableColumns);

    if (arrayValidation(myColumns)) {
      const newMyColumns = myColumns.filter(
        (item) => item.apiField !== column.apiField,
      );
      setMyColumns(newMyColumns);
    }
  };

  useEffect(() => {
    startProcessByColumns(dataColumns?.columns || []);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const body = (
    <>
      <Grid container>
        <DialogHeader
          tableName={tableName}
          handleSubmit={handleSubmit}
          handleReset={handleReset}
          handleDelete={handleDelete}
          showDeleteIcon={showConfigName && configurationName}
        />
        <Grid item xs={12} style={{ margin: 24 }}>
          <Grid container spacing={6}>
            {showConfigName ? (
              <Grid item xs={12} md={12} lg={12}>
                <CustomTextField
                  required
                  label="Nombre de reporte"
                  name={'configName'}
                  onChange={handleChange}
                  // onBlur={handleBlur}
                  value={configName}
                  error={Boolean(isSubmitting && errorConfigName)}
                  helperText2={errorConfigName}
                  inputProps={{
                    autoComplete: 'off',
                  }}
                  fullWidth
                />
              </Grid>
            ) : null}

            <Grid item xs={6} md={6} lg={6}>
              <AvailableColumns
                columns={availableColumns}
                onClick={handleChangeAvailableColumns}
                showConfigName={showConfigName}
              />
            </Grid>
            <Grid item xs={6} md={6} lg={6}>
              <MyColumns
                columns={myColumns}
                onClick={handleChangeMyColumns}
                moveRows={moveRows}
                showConfigName={showConfigName}
              />
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </>
  );

  return (
    <CustomDialog
      open={open}
      handleClose={handleClose}
      body={body}
      fullWidth={true}
      sx={styles.dialog}
    />
  );
};

export default ConfigurationTableModal;
